import { useGetAllAgencies } from '@urbanx/agx.agencies.client';
import {
  BlankContractStatuses,
  CampaignAdminModelMarketingStatus,
  CampaignDashboardRow,
  CampaignStatuses,
  LaunchDateStatuses,
  MarketingStatuses,
  SortingOption,
  useStaffGetCampaignDashboard,
} from '@urbanx/agx.campaigns.client';
import type { SelectChipOption, SortOrder } from '@urbanx/agx.ui-components2';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { splitPascalCase } from 'utils/stringUtils';
import { getStatusConfig } from '../components/campaignMarketing/CampaignFlowStatusBadge';
import {
  createInitialCampaignsDashboardFilterState,
  storeCampaignsDashboardFilterState,
} from './CampaignsDashboardFilterState';

const PAGE_SIZE = 20;

export interface AgencyLookupValue {
  name: string;
  logoUrl?: string;
}

const createAgencyLookup = (
  agencies?: { id?: string; name?: string; logoUrl?: string }[]
): Record<string, AgencyLookupValue> =>
  (agencies ?? []).reduce(
    (lookup, agency) => {
      if (agency.id && agency.name) {
        lookup[agency.id] = {
          name: agency.name,
          logoUrl: agency.logoUrl,
        };
      }

      return lookup;
    },
    {} as Record<string, AgencyLookupValue>
  );

const campaignStatusOptions = Object.values(CampaignStatuses).map((status) => ({
  label: splitPascalCase(status).replace(/^(Agreement|Listing)\s+/, ''),
  value: status,
}));

const marketingStatusOptions = Object.values(MarketingStatuses).map((status) => ({
  label:
    getStatusConfig(status as unknown as CampaignAdminModelMarketingStatus)?.text ??
    splitPascalCase(status),
  value: status,
}));

const contractStatusOptions = Object.values(BlankContractStatuses).map((status) => ({
  label: splitPascalCase(status),
  value: status,
}));

const launchDateStatusOptions = [
  {
    label: 'This week',
    value: LaunchDateStatuses.ThisWeek,
  },
  {
    label: 'Next week',
    value: LaunchDateStatuses.NextWeek,
  },
  {
    label: 'Launch date not set',
    value: LaunchDateStatuses.LaunchDateNotSet,
  },
  {
    label: 'Off market',
    value: LaunchDateStatuses.OffMarket,
  },
];

const buildSelectedOptions = (
  options: { label: string; value: string }[],
  selectedValues?: string[]
) => {
  return options.map((option) => ({
    ...option,
    selected: selectedValues?.includes(option.value) ?? false,
  }));
};

const useCampaignsDashboard = () => {
  const [initialFilterState] = useState(createInitialCampaignsDashboardFilterState);
  const { data: agencies, isPending: isAgenciesPending } = useGetAllAgencies();
  const [selectedAgencyIds, setSelectedAgencyIds] = useState<string[] | undefined>(
    initialFilterState.agencyIds
  );
  const [selectedCampaignStatuses, setSelectedCampaignStatuses] = useState<
    CampaignStatuses[] | undefined
  >(initialFilterState.campaignStatuses);
  const [selectedBlankContractStatuses, setSelectedBlankContractStatuses] = useState<
    BlankContractStatuses[] | undefined
  >(initialFilterState.blankContractStatuses);
  const [selectedLaunchDateStatuses, setSelectedLaunchDateStatuses] = useState<
    LaunchDateStatuses[] | undefined
  >(initialFilterState.launchDateStatuses);
  const [selectedMarketingStatuses, setSelectedMarketingStatuses] = useState<
    MarketingStatuses[] | undefined
  >(initialFilterState.marketingStatuses);
  const [selectedSortingOption, setSelectedSortingOption] = useState<SortingOption | undefined>(
    SortingOption.LaunchDateAsc
  );
  const [addressSearchText, setAddressSearchText] = useState('');
  const [continuationToken, setContinuationToken] = useState<string | undefined>(undefined);
  const [rows, setRows] = useState<CampaignDashboardRow[]>([]);
  const [nextContinuationToken, setNextContinuationToken] = useState<string | undefined>(undefined);
  const [lastHandledToken, setLastHandledToken] = useState<string | null>(null);
  const [lastRowElement, setLastRowElement] = useState<HTMLDivElement | null>(null);
  const [isLastRowVisible, setIsLastRowVisible] = useState(false);

  const { data, isPending } = useStaffGetCampaignDashboard(
    selectedAgencyIds,
    selectedCampaignStatuses,
    selectedLaunchDateStatuses,
    selectedMarketingStatuses,
    selectedBlankContractStatuses,
    addressSearchText || undefined,
    continuationToken,
    PAGE_SIZE,
    selectedSortingOption
  );

  useEffect(() => {
    setContinuationToken(undefined);
    setRows([]);
    setNextContinuationToken(undefined);
    setLastHandledToken(null);
    setLastRowElement(null);
    setIsLastRowVisible(false);
  }, [
    selectedAgencyIds,
    addressSearchText,
    selectedBlankContractStatuses,
    selectedCampaignStatuses,
    selectedLaunchDateStatuses,
    selectedMarketingStatuses,
    selectedSortingOption,
  ]);

  useEffect(() => {
    storeCampaignsDashboardFilterState({
      agencyIds: selectedAgencyIds,
      campaignStatuses: selectedCampaignStatuses,
      blankContractStatuses: selectedBlankContractStatuses,
      launchDateStatuses: selectedLaunchDateStatuses,
      marketingStatuses: selectedMarketingStatuses,
    });
  }, [
    selectedAgencyIds,
    selectedBlankContractStatuses,
    selectedCampaignStatuses,
    selectedLaunchDateStatuses,
    selectedMarketingStatuses,
  ]);

  useEffect(() => {
    const requestToken = continuationToken ?? '__initial__';
    if (!data || lastHandledToken === requestToken) return;

    setRows((prev) => {
      const nextRows = continuationToken
        ? [...prev, ...(data.results ?? [])]
        : [...(data.results ?? [])];

      return nextRows.filter((row, idx, arr) => {
        return arr.findIndex((item) => item.campaignId === row.campaignId) === idx;
      });
    });
    setNextContinuationToken(data.continuationToken);
    setLastHandledToken(requestToken);
  }, [continuationToken, data, lastHandledToken]);

  const loadMore = useCallback(() => {
    if (isPending) return;
    if (!nextContinuationToken) return;

    setContinuationToken(nextContinuationToken);
  }, [isPending, nextContinuationToken]);

  const lastRowRef = useCallback((node: HTMLDivElement | null) => {
    setLastRowElement(node);
  }, []);

  useEffect(() => {
    if (!lastRowElement) {
      setIsLastRowVisible(false);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setIsLastRowVisible(entries[0].isIntersecting);
      },
      {
        root: null,
        rootMargin: '0px 0px 300px 0px',
        threshold: 0,
      }
    );

    observer.observe(lastRowElement);

    return () => observer.disconnect();
  }, [lastRowElement]);

  useEffect(() => {
    if (!isLastRowVisible) return;
    if (!nextContinuationToken) return;
    if (isPending) return;

    loadMore();
  }, [isPending, isLastRowVisible, loadMore, nextContinuationToken]);

  const agencyLookup = useMemo(() => createAgencyLookup(agencies), [agencies]);

  const onSortLaunch = useCallback((sortOrder: SortOrder) => {
    const nextSortingOption =
      sortOrder === 1
        ? SortingOption.LaunchDateAsc
        : sortOrder === -1
          ? SortingOption.LaunchDateDesc
          : undefined;

    setSelectedSortingOption(nextSortingOption);
  }, []);

  const agencyFilterOptions = useMemo(() => {
    return (agencies ?? []).map((agency) => ({
      label: agency.name ?? '',
      value: agency.id ?? '',
      selected: selectedAgencyIds?.includes(agency.id ?? '') ?? false,
    }));
  }, [agencies, selectedAgencyIds]);

  const selectedCampaignStatusOptions = useMemo(() => {
    return buildSelectedOptions(campaignStatusOptions, selectedCampaignStatuses);
  }, [selectedCampaignStatuses]);

  const selectedContractStatusOptions = useMemo(() => {
    return buildSelectedOptions(contractStatusOptions, selectedBlankContractStatuses);
  }, [selectedBlankContractStatuses]);

  const selectedLaunchDateStatusOptions = useMemo(() => {
    return buildSelectedOptions(launchDateStatusOptions, selectedLaunchDateStatuses);
  }, [selectedLaunchDateStatuses]);

  const selectedMarketingStatusOptions = useMemo(() => {
    return buildSelectedOptions(marketingStatusOptions, selectedMarketingStatuses);
  }, [selectedMarketingStatuses]);

  const handleAgencyFilterSelect = useCallback((options: SelectChipOption[]) => {
    setSelectedAgencyIds(options.length > 0 ? options.map((option) => option.value) : undefined);
  }, []);

  const handleCampaignStatusFilterSelect = useCallback((options: SelectChipOption[]) => {
    setSelectedCampaignStatuses(
      options.length > 0 ? options.map((option) => option.value as CampaignStatuses) : undefined
    );
  }, []);

  const handleContractFilterSelect = useCallback((options: SelectChipOption[]) => {
    setSelectedBlankContractStatuses(
      options.length > 0
        ? options.map((option) => option.value as BlankContractStatuses)
        : undefined
    );
  }, []);

  const handleLaunchDateFilterSelect = useCallback((options: SelectChipOption[]) => {
    setSelectedLaunchDateStatuses(
      options.length > 0 ? options.map((option) => option.value as LaunchDateStatuses) : undefined
    );
  }, []);

  const handleMarketingFilterSelect = useCallback((options: SelectChipOption[]) => {
    setSelectedMarketingStatuses(
      options.length > 0 ? options.map((option) => option.value as MarketingStatuses) : undefined
    );
  }, []);

  return {
    rows,
    agencyLookup,
    isPending,
    isAgenciesPending,
    lastRowRef,
    addressSearchText,
    agencyFilterOptions,
    selectedCampaignStatusOptions,
    selectedContractStatusOptions,
    selectedLaunchDateStatusOptions,
    selectedMarketingStatusOptions,
    setAddressSearchText,
    handleAgencyFilterSelect,
    handleCampaignStatusFilterSelect,
    handleContractFilterSelect,
    handleLaunchDateFilterSelect,
    handleMarketingFilterSelect,
    onSortLaunch,
    resetAgencyFilters: () => setSelectedAgencyIds(undefined),
    resetCampaignStatusFilters: () => setSelectedCampaignStatuses(undefined),
    resetContractFilters: () => setSelectedBlankContractStatuses(undefined),
    resetLaunchDateFilters: () => setSelectedLaunchDateStatuses(undefined),
    resetMarketingFilters: () => setSelectedMarketingStatuses(undefined),
  };
};

export default useCampaignsDashboard;
